import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../firebase"
import SEO from "../../components/common/SEO"
import { Globe, ArrowRight, Sparkles } from "lucide-react"
import "./destinations.css"

const Destinations = () => {
    const [countries, setCountries] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "countries"))
                const list = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                setCountries(list)
            } catch (error) {
                console.error("Error fetching countries:", error)
            } finally {
                setLoading(false)
            }
        }
        fetchCountries()
    }, [])

    const defaultCountries = [
        { id: "uk", name: "United Kingdom", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197374.png" },
        { id: "canada", name: "Canada", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197430.png" },
        { id: "usa", name: "USA", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197484.png" },
        { id: "australia", name: "Australia", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197507.png" },
        { id: "germany", name: "Germany", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197571.png" },
        { id: "france", name: "France", flagUrl: "https://cdn-icons-png.flaticon.com/128/197/197560.png" },
    ]

    const displayCountries = countries.length > 0 ? countries : defaultCountries

    return (
        <div className="destinations-page">
            <SEO
                title="Global Destinations - Study Abroad Programs"
                description="Explore top study abroad destinations including UK, USA, Canada, Australia, and more. Expert guidance for international education."
                url="/study-abroad"
            />

            <div className="destinations-hero">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hero-badge"
                    >
                        <Globe size={18} />
                        <span>World-Class Education</span>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Choose Your <span className="text-gradient">Dream Destination</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        Explore the best countries for international students and start your journey towards a global career.
                    </motion.p>
                </div>
            </div>

            <div className="container">
                <div className="destinations-grid">
                    {displayCountries.map((country, idx) => (
                        <motion.div
                            key={country.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                        >
                            <Link to={`/country/${country.id}`} className="destination-card">
                                <div className="card-image-box">
                                    <img src={country.heroImage || `https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=2070&auto=format&fit=crop`} alt={country.name} />
                                    <div className="card-overlay" />
                                    <img src={country.flagUrl} alt={`${country.name} flag`} className="dest-flag" />
                                </div>
                                <div className="card-info">
                                    <h3>{country.name}</h3>
                                    <div className="card-footer">
                                        <span>Explore Opportunities</span>
                                        <ArrowRight size={18} />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    className="destinations-cta"
                >
                    <div className="cta-glass">
                        <h2>Ready to Start Your Journey?</h2>
                        <p>Our expert counsellors are here to guide you through every step of the process.</p>
                        <a href="https://wa.me/9115400982" className="cta-btn">
                            Talk to an Expert <Sparkles size={20} />
                        </a>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}

export default Destinations
